import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { t } from 'i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { StyledFormControl, StyledFormHelperText, StyledInputLabel } from '../Dropdown/style';

const MultiSelect = ({
  name,
  options,
  placeholder,
  label,
  required = false,
  selectedValues = [],
  onChange,
  useHookForm = false,
  disabled = false,
  forCustomField = false
}) => {
  // Always call useFormContext, but only use it if useHookForm is true
  const hookFormContext = useFormContext();
  const control = useHookForm ? hookFormContext.control : null;
  const errors = useHookForm ? hookFormContext.formState.errors : {};
  const clearErrors = useHookForm ? hookFormContext.clearErrors : null;
  const trigger = useHookForm ? hookFormContext.trigger : null;
  const hasError = useHookForm && !!errors?.[name];
  const helperText = useHookForm ? errors?.[name]?.message || '' : '';

  const renderSelect = (field) => (
    <Select
      multiple
      value={useHookForm ? (Array.isArray(field?.value) ? field.value : []) : selectedValues}
      onChange={
        useHookForm
          ? (event) => {
              const values = event.target.value;
              if (Array.isArray(values)) {
                field.onChange(values);
                trigger?.(name);
                if (values.length > 0) {
                  clearErrors?.(name);
                }
                if (onChange) onChange(event);
              }
            }
          : onChange
      }
      onBlur={
        useHookForm
          ? () => {
              field?.onBlur();
              trigger?.(name);
            }
          : undefined
      }
      displayEmpty
      IconComponent={ExpandMoreIcon}
      sx={{
        backgroundColor: 'white',
        '& .MuiSelect-icon': {
          fontSize: '2rem'
        }
      }}
      inputProps={{
        endAdornment: hasError && (
          <InputAdornment position="end">
            <ErrorOutlineIcon sx={{ color: 'red' }} />
          </InputAdornment>
        )
      }}
      renderValue={(selected) => {
        const textClass = disabled ? 'disabledText' : '';
        if (selected.length === 0) {
          return (
            <>
              <Typography
                variant="body2"
                className={`placeholder-text ${textClass}`}
                color="text.secondary">
                {placeholder}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length > 1 ? (
                  <Typography
                    variant="body2"
                    className={textClass}>{`${selected.length} selected`}</Typography>
                ) : (
                  selected.map((value) => (
                    <Typography key={value} variant="body2" className={textClass}>
                      {value
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </Typography>
                  ))
                )}
              </Box>
            </>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.length > 1 ? (
              <Typography
                variant="body2"
                className={textClass}>{`${selected.length} selected`}</Typography>
            ) : (
              selected.map((current) => {
                const findSelected = options?.find((option) => {
                  const formatValue = option.value ? option.value : option.id;
                  const val = forCustomField ? formatValue : t(formatValue);

                  return current === val;
                });

                const formatLabel = findSelected?.label ? findSelected.label : findSelected?.name;
                const lab = forCustomField ? formatLabel : t(formatLabel);
                return (
                  <Typography key={formatLabel} variant="body2" className={textClass}>
                    {lab}
                  </Typography>
                );
              })
            )}
          </Box>
        );
      }}>
      {options &&
        options?.map((option) => {
          const formatValue = option.value ? option.value : option.id;
          const formatLabel = option.label ? option.label : option.name;
          option.value ? option.value : option.id;
          option.value ? option.value : option.id;

          const val = forCustomField ? formatValue : t(formatValue);
          const lab = forCustomField ? formatLabel : t(formatLabel);

          return (
            <MenuItem
              key={forCustomField ? option.value : t(option.value)}
              value={val}
              disabled={disabled}>
              <Checkbox
                disabled={disabled}
                checked={
                  useHookForm ? field?.value?.indexOf(val) > -1 : selectedValues.indexOf(val) > -1
                }
                sx={{
                  marginLeft: '-8px',
                  transform: useHookForm ? 'scale(1.5)' : 'none'
                }}
              />
              <ListItemText primary={lab} />
            </MenuItem>
          );
        })}
    </Select>
  );

  return useHookForm ? (
    <>
      <StyledInputLabel htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </StyledInputLabel>
      <StyledFormControl fullWidth error={hasError}>
        <Controller
          name={name}
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <>
              {renderSelect(field)}
              <StyledFormHelperText>{helperText}</StyledFormHelperText>
            </>
          )}
        />
      </StyledFormControl>
    </>
  ) : (
    <FormControl size="small" sx={{ marginRight: '16px', width: '200px' }}>
      {renderSelect()}
    </FormControl>
  );
};

export default MultiSelect;
